import { IABMasterSlotData, IABTest, IABTestSystem, IABTestVariation } from './abtest-interfaces';
import { getQueryString } from '../url-provider';
import { getCookie, getLocalStorageItem, setLocalStorageItem } from '../../DAL/storage-provider';

/*
Usage:
-------
const abtest = getABTest('NeoWizeListRecs9019');

if(abtest.isActive){
    sendABTestAnalytics(abtest);
    // sendABTestAnalytics(abtest, 'unavailable object');

    console.log(abtest.selected);
}
 */

/**
 * Allows to TypeScript recognize abtests namespace
 */
declare global {
    interface Window {
        abtests: any;
    }
}

export enum ABTestTypeEnum{
    Client = 0,
    Server = 1
}

// const EXPIRATION = 2000000000;
const CLIENT_SIDE_LOCAL_STORAGE_NAME = 'waves_abtest';
const SERVER_SIDE_COOKIE_NAME = 'waves_abtest_server';

/**
 * handle the case when test has a master with selected "off" variant but their variant percents are different
 */
const handlePartialMasterVariants = (test: IABTest) => {
    if(!test.variations) return;

    let sum = 0;
    const offItems = [];

    for(let i=0; i<test.variations.length; i++){

        const variant = test.variations[i];

        if(!(variant.name === 'on' || variant.name.startsWith('on-'))){
            sum += variant.percent;
            offItems.push(variant);
        }
        else{
            variant.percent = 0;
        }
    }

    const proportion = (sum > 0) ? (100 / sum) : 0;

    for(let i=0; i<offItems.length; i++){

        const offItem = offItems[i];
        offItem.percent = Math.round(proportion * offItem.percent);
    }
};

/**
 * if given 2 tests, check if their percent values are the same
 * returns {boolean}: if true -> the same, otherwise different
 */
const compareTestsVariants = (test1: IABTest, test2: IABTest) => {

    const test1Variations = test1.variations || [];
    const test2Variations = test2.variations || [];

    const values1 = test1Variations.map(item => item.percent);

    values1.sort((a, b) => {
        return a - b;
    });

    const values2 = test2Variations.map(item => item.percent);

    values2.sort((a, b) => {
        return a - b;
    });

    return values1.join(' ') === values2.join(' ');
};

/**
 * choose variation for 1 test
 */
const chooseVariation = (test: IABTest) => {

    const notRandomNumbers = [];

    if(!test || !test.variations) return;

    for(let i=0; i<test.variations.length; i++){

        //get variation
        const variation = test.variations[i];

        for(let j=0; j<variation.percent * 100; j++){
            notRandomNumbers.push(variation.name);
        }
    }

    //get probable index
    const idx = Math.floor(Math.random() * notRandomNumbers.length);

    test.selected = notRandomNumbers[idx];
};

/**
 * get variation objects list from string
 * variations - variation1:percent1, variation2:percent2, ...
 * returns test variations as list of objects
 */
const parseVariations = (variations: string): IABTestVariation[] => {

    if(!variations) return [];

    const testVariations: IABTestVariation[] = [];

    const list = variations.split(',');

    for(let i=0; i<list.length; i++){

        // get item: variationN:percentN
        const listItem = list[i].trim();

        const variationItemList = listItem.split(':');

        if(variationItemList.length === 2){

            testVariations.push({
                name: variationItemList[0].trim()
                ,percent: Number(variationItemList[1])/100 || 0
            });
        }
    }

    return testVariations;
};

/**
 * get tests printed as JSON in HTML
 */
const getTestsFromHTML = (isServer: boolean): IABTest[] => {

    const $data = document.getElementById('abtest-data') as HTMLElement;
    if(!$data) return [];

    const result: IABTest[] = [];
    let data = [];

    try{
        data = JSON.parse($data.textContent || '');
    }
    catch(ex){}

    if(!data) return [];

    for(let i=0; i<data.length; i++){

        const dataItem = data[i];

        const copy: IABTest = {
            codeName: dataItem.codeName,
            isActive: (dataItem.isActive + '').toLowerCase() === 'true',
            masterSlotID: Number(dataItem.masterSlotID) || 0, // "0"
            slot: Number(dataItem.slot) || 0, // "6"
            testType: Number(dataItem.testType) || 0, // "0" or "1"
            variations: parseVariations(dataItem.variations.trim()),

            // client side tests should be started by default
            // for client side tests 'started' property is not returned from server and should be generated here on the client
            started: dataItem.started === undefined ? true : (dataItem.started + '').toLowerCase() === 'true' // "True"
        };

        const shouldAddItem = (isServer && copy.testType === ABTestTypeEnum.Server) || (!isServer && copy.testType === ABTestTypeEnum.Client);

        if(shouldAddItem){
            result.push(copy);
        }
    }

    return result;
};

/**
 * try to get client side tests from url
 * http://dev.waves.com/bundles/jjp-analog-legends?abtest=ProductPageTopBanner-show_LightboxTest-val
 */
const getClientSideTestsFromURL = () => {

    if (!window.location.search || window.location.search.indexOf('abtest') === -1) return [];

    const abtestString = getQueryString()['abtest'];

    if(!abtestString) return [];

    const tests = [];
    const testsArr = abtestString.split('_');

    for(let i=0; i<testsArr.length; i++) {

        //get test item
        const testItem = testsArr[i];

        //get test data
        const testData = testItem.split('-');

        if(testData.length === 2){
            tests.push({
                codeName: testData[0]
                ,selected: testData[1]
                ,isActive: true
            });
        }
    }

    return tests;
};

/**
 * find old tests that appear in new tests already and add them (they have selected value already)
 */
const findOldTestsThatAppearInNewTests = (updatedTests: IABTest[], savedTests: IABTest[], newTests: IABTest[]) => {

    for(let i=0; i<savedTests.length; i++){

        //get saved test
        const savedTest = savedTests[i];

        for(let j=0; j<newTests.length; j++){

            //get new test
            const newTest = newTests[j];

            if(newTest.codeName.trim() === savedTest.codeName.trim() && newTest.isActive === true){
                updatedTests.push(savedTest);
                break;
            }
        }
    }
};

/**
 * find new tests that don't appear in old tests
 */
const findNewTestsThankDontAppearInOldTests = (updatedTests: IABTest[], savedTests: IABTest[], newTests: IABTest[]) => {

    for(let i=0; i<newTests.length; i++){

        //get new test
        const newTest = newTests[i];

        //set flag to 'not appear'
        let ifAppear = false;

        for(let j=0; j<savedTests.length; j++){

            //get saved test
            const savedTest = savedTests[j];

            if(newTest.codeName.trim() === savedTest.codeName.trim()){

                //test appears...
                ifAppear = true;
                break;
            }
        }

        if(!ifAppear && newTest.isActive === true){

            //add test to list
            updatedTests.push(newTest);
        }
    }
};

/**
 * choose variations
 * updatedTests - merged from storage + new tests
 */
const chooseVariations = (updatedTests: IABTest[]) => {

    const masterSlotIDs: IABMasterSlotData = {};
    const regular = [];

    // get updated tests and split them to 2 groups: master and non-master
    for(let i=0; i<updatedTests.length; i++){

        const test = updatedTests[i];

        const masterSlotID = Number(test.masterSlotID) || 0;

        if(masterSlotID !== 0){

            const masterData = masterSlotIDs[masterSlotID] || {
                list: []
            };

            masterData.list.push(test);

            masterSlotIDs[masterSlotID] = masterData;
        }
        else{
            regular.push(test);
        }
    }

    //choose variation for all pages that don't have a master and not selected yet (such pages includes all master tests)
    for(let i=0; i<regular.length; i++){

        const test = regular[i];

        if(test.selected === undefined){
            chooseVariation(test);
        }

        const slot = Number(test.slot) || 0;

        if(masterSlotIDs[slot] !== undefined){

            masterSlotIDs[slot].test = test;
        }
    }

    //chose variation for every tests that have a master and not selected yet
    for(let masterSlotID in masterSlotIDs){

        const masterData = masterSlotIDs[masterSlotID];

        if(!masterData || !masterData.list || !masterData.test) continue;

        for(let i=0; i<masterData.list.length; i++){

            const test = masterData.list[i];

            if(test.selected === undefined){

                const theSameVariantValues = compareTestsVariants(test, masterData.test);

                if(theSameVariantValues){

                    //update test.selected with master selected
                    test.selected = masterData.test.selected;
                }
                else{

                    if(masterData.test.selected === 'off'){

                        // handle the case when test has a master with selected "off" variant but their variant percents are different
                        handlePartialMasterVariants(test);
                    }

                    chooseVariation(test);
                }
            }
        }
    }


    return updatedTests;
};

/**
 * get updated tests: merge 'saved in storage' tests with the new tests
 */
const mergeTests = (savedTests: IABTest[], newTests: IABTest[]) => {

    if(!savedTests || !Array.isArray(savedTests)){
        savedTests = [];
    }

    if(!newTests || !Array.isArray(newTests)){
        newTests = [];
    }

    let updatedTests: IABTest[] = [];

    //find old tests that appear in new tests already and add them (they have selected value already)
    findOldTestsThatAppearInNewTests(updatedTests, savedTests, newTests);

    //find new tests that don't appear in old tests
    findNewTestsThankDontAppearInOldTests(updatedTests, savedTests, newTests);

    //choose variations
    updatedTests = chooseVariations(updatedTests);

    return updatedTests;
};

/**
 * client side tests init
 */
const initClientSideTests = () => {

    // get test data printed as JSON in HTML
    const newClientSideTests = getTestsFromHTML(false);

    // try to get value from storage
    let savedClientSideTests: IABTest[] = [];
    let savedClientSideTestsStr = getLocalStorageItem(CLIENT_SIDE_LOCAL_STORAGE_NAME) as string;

    try{
        savedClientSideTests = JSON.parse(savedClientSideTestsStr);

        /*
        if(typeof savedClientSideTests === 'string'){
            savedClientSideTests = JSON.parse(savedClientSideTests);
        }*/
    }
    catch(ex){
        savedClientSideTests = [];
    }

    // merge client side tests and get updated tests
    const clientSideTests = mergeTests(savedClientSideTests, newClientSideTests);

    setLocalStorageItem(CLIENT_SIDE_LOCAL_STORAGE_NAME, clientSideTests);

    return clientSideTests;
};

/**
 * server side tests init
 */
const initServerSideTests = () => {

    // try to get value from cookies
    let serverSideTestsStr = getCookie(SERVER_SIDE_COOKIE_NAME);
    let serverSideTests: IABTest[] = [];

    if(!serverSideTestsStr) return [];

    try{
        serverSideTests = JSON.parse(serverSideTestsStr) as IABTest[];
    }
    catch(ex){
        serverSideTests = [];
    }

    return serverSideTests;
};

/**
 * init A/B test system
 */
export const initABTestSystem = () : IABTestSystem => {

    // change tests since the last time
    let clientSideTests: IABTest[] = [];
    let serverSideTests: IABTest[] = [];

    // try to get client side tests from url;
    // used for debug;
    const urlTests = getClientSideTestsFromURL();

    if(!urlTests || urlTests.length <= 0){

        clientSideTests = initClientSideTests();
        serverSideTests = initServerSideTests();
    }

    return {
        clientSideTests,
        serverSideTests,
        urlTests,
    }
};

/**
 * get test by test name
 */
export const getABTest = (testName: string) => {

    if(!window.abtests){
        window.abtests = initABTestSystem() as IABTestSystem;
    }

    if(!window.abtests) return null;

    const urlTest = window.abtests.urlTests.find((test: IABTest) => test.codeName.toLowerCase().trim() === testName.toLowerCase().trim());

    if(urlTest && urlTest.isActive) return urlTest;

    const clientSideTest = window.abtests.clientSideTests.find((test: IABTest) => test.codeName.toLowerCase().trim() === testName.toLowerCase().trim());

    if(clientSideTest && clientSideTest.isActive) return clientSideTest;

    const serverSideTest = window.abtests.serverSideTests.find((test: IABTest) => test.codeName.toLowerCase().trim() === testName.toLowerCase().trim());

    if(serverSideTest && serverSideTest.isActive && serverSideTest.started) return serverSideTest;

    return null;
};

/**
 * send data to google analytics
 */
export const sendABTestAnalytics = (_test: IABTest, selected = '') => {

    if (!_test || !window.dataLayer) return;

    const test = {..._test};

    if(selected !== ''){
        test.selected = selected;
    }

    // send data to google analytics
    window.dataLayer.push({
        'testName': test.codeName
        , 'testVariation': test.selected
        , 'testSlot': test.slot
        , 'event': 'user entered test'
    });

    // some tests have master slot ID not equal to 0
    const masterSlotID = Number(test.masterSlotID) || 0;

    if (masterSlotID === 0 || !window.abtests || !window.abtests.clientSideTests) return;

    // find all tests with masterSlotID property equal to the given master
    const found = window.abtests.clientSideTests.filter((item: IABTest) => {
        const slot = Number(item.slot) || 0;
        return masterSlotID === slot;
    });

    if (!found || found.length <= 0) return;

    // we suppose that there is only one master with the given slot number
    const master = found[0];

    // check if master and test have the same variants
    const theSameVariantValues = compareTestsVariants(test, master);

    if(!theSameVariantValues) return;

    //send master data to GA
    window.dataLayer.push({
        'testName': master.codeName
        ,'testVariation': master.selected
        ,'testSlot': master.slot
        ,'event': 'user entered test'
    });
};